interface EvaluationJSON {
  [model: string]: {
    [subtask: string]: {
      responses?: { [queryId: string]: any };
      score?: any;
    };
    metadata?: any;
    score?: any;
  };
}

interface QuerySetMap {
  [subtask: string]: Map<string, string[]>;
}

export interface EvaluationStatistics {
  num_models: number;
  num_subtasks: number;
  queries_per_subtask: { [subtask: string]: number };
  total_queries: number;
  models: string[];
}

export interface ParseResult {
  filteredJson: EvaluationJSON;
  statistics: EvaluationStatistics;
  excludedModels: string[];
}

/**
 * Validate the evaluation JSON structure
 */
function validateEvaluationJSON(responseJson: any): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Check if it's an object
  if (typeof responseJson !== 'object' || responseJson === null) {
    errors.push('JSON must be an object with model names as keys');
    return { isValid: false, errors };
  }

  const models = Object.keys(responseJson);
  if (models.length === 0) {
    errors.push('JSON must contain at least one model');
    return { isValid: false, errors };
  }

  // Check each model structure
  for (const model of models) {
    const modelData = responseJson[model];
    if (typeof modelData !== 'object' || modelData === null) {
      errors.push(`Model "${model}" must be an object`);
      continue;
    }

    const subtasks = Object.keys(modelData).filter(
      (key) => key !== 'metadata' && key !== 'score'
    );

    if (subtasks.length === 0) {
      errors.push(`Model "${model}" must contain at least one subtask`);
    }

    // Check subtask structure
    for (const subtask of subtasks) {
      const subtaskData = modelData[subtask];
      if (typeof subtaskData !== 'object' || subtaskData === null) {
        errors.push(
          `Model "${model}", subtask "${subtask}" must be an object`
        );
        continue;
      }

      if (!subtaskData.responses) {
        errors.push(
          `Model "${model}", subtask "${subtask}" must have a "responses" field`
        );
        continue;
      }

      if (
        typeof subtaskData.responses !== 'object' ||
        subtaskData.responses === null
      ) {
        errors.push(
          `Model "${model}", subtask "${subtask}": "responses" must be an object`
        );
        continue;
      }

      if (Object.keys(subtaskData.responses).length === 0) {
        errors.push(
          `Model "${model}", subtask "${subtask}": "responses" must contain at least one query`
        );
      }
    }
  }

  return { isValid: errors.length === 0, errors };
}

/**
 * Parse benchmark JSON, find largest group of models with identical query sets,
 * and return statistics.
 */
export function parseEvaluationJSON(responseJson: EvaluationJSON): ParseResult {
  // First validate the structure
  const validation = validateEvaluationJSON(responseJson);
  if (!validation.isValid) {
    throw new Error(
      `Invalid JSON format:\n${validation.errors.map((e, i) => `${i + 1}. ${e}`).join('\n')}`
    );
  }

  const allModels = Object.keys(responseJson);

  // Create query sets for each model and subtask
  const modelQuerySets: {
    [model: string]: { [subtask: string]: Set<string> };
  } = {};

  for (const model of Object.keys(responseJson)) {
    modelQuerySets[model] = {};
    for (const subtask of Object.keys(responseJson[model])) {
      if (subtask !== 'metadata' && subtask !== 'score') {
        const responses = responseJson[model][subtask].responses || {};
        const queryIds = new Set(Object.keys(responses));
        modelQuerySets[model][subtask] = queryIds;
      }
    }
  }

  // Find unique query sets per subtask and group models by them
  const subtaskGroups: {
    [subtask: string]: Map<string, string[]>;
  } = {};

  for (const [model, subtasks] of Object.entries(modelQuerySets)) {
    for (const [subtask, querySet] of Object.entries(subtasks)) {
      if (!subtaskGroups[subtask]) {
        subtaskGroups[subtask] = new Map();
      }

      // Create a string key from the query set for grouping
      const querySetKey = Array.from(querySet).sort().join(',');

      if (!subtaskGroups[subtask].has(querySetKey)) {
        subtaskGroups[subtask].set(querySetKey, []);
      }

      subtaskGroups[subtask].get(querySetKey)!.push(model);
    }
  }

  // For each subtask, find the largest group
  let modelsToKeep = new Set<string>();
  let firstSubtask = true;

  for (const subtask of Object.keys(subtaskGroups).sort()) {
    // Find the group with the most models
    let largestGroupKey: string = '';
    let largestGroupModels: string[] = [];
    let maxSize = 0;

    // Convert Map to array to iterate
    const entries = Array.from(subtaskGroups[subtask].entries());
    for (let i = 0; i < entries.length; i++) {
      const [querySetKey, models] = entries[i];
      if (models.length > maxSize) {
        maxSize = models.length;
        largestGroupKey = querySetKey;
        largestGroupModels = models;
      }
    }

    if (largestGroupModels.length > 0) {
      const numQueries = largestGroupKey ? largestGroupKey.split(',').length : 0;

      console.log(
        `${subtask}. Largest group: ${largestGroupModels.length} models with ${numQueries} queries`
      );

      // Intersect with models to keep
      if (firstSubtask) {
        modelsToKeep = new Set(largestGroupModels);
        firstSubtask = false;
      } else {
        const modelsArray = Array.from(modelsToKeep);
        modelsToKeep = new Set(
          modelsArray.filter((m) => largestGroupModels.includes(m))
        );
      }
    }
  }

  console.log('='.repeat(80));
  console.log(
    `\nModels to keep (in largest group for ALL subtasks): ${modelsToKeep.size}`
  );

  if (modelsToKeep.size === 0) {
    console.warn('WARNING: No models are in the largest group for all subtasks!');
  }

  const excludedModels = allModels
    .filter((m) => !modelsToKeep.has(m))
    .sort();

  // Filter response_json to only keep selected models
  const filteredResponseJson: EvaluationJSON = {};
  for (const [model, data] of Object.entries(responseJson)) {
    if (modelsToKeep.has(model)) {
      filteredResponseJson[model] = data;
    }
  }

  console.log(
    `\nFiltered from ${Object.keys(responseJson).length} to ${Object.keys(filteredResponseJson).length} models`
  );
  console.log('='.repeat(80));

  // Calculate statistics
  const queriesPerSubtask: { [subtask: string]: number } = {};

  for (const model of Object.keys(filteredResponseJson)) {
    for (const subtask of Object.keys(filteredResponseJson[model])) {
      if (subtask !== 'metadata' && subtask !== 'score') {
        if (!queriesPerSubtask[subtask]) {
          const responses =
            filteredResponseJson[model][subtask].responses || {};
          queriesPerSubtask[subtask] = Object.keys(responses).length;
        }
      }
    }
  }

  const totalQueries = Object.values(queriesPerSubtask).reduce(
    (sum, count) => sum + count,
    0
  );

  // Return results
  const statistics: EvaluationStatistics = {
    num_models: modelsToKeep.size,
    num_subtasks: Object.keys(queriesPerSubtask).length,
    queries_per_subtask: queriesPerSubtask,
    total_queries: totalQueries,
    models: Array.from(modelsToKeep).sort(),
  };

  return {
    filteredJson: filteredResponseJson,
    statistics,
    excludedModels,
  };
}

export const EXPECTED_JSON_FORMAT_EXAMPLE = {
  "model_name_1": {
    "subtask_1": {
      "responses": {
        "query_id_1": "response_text",
        "query_id_2": "response_text"
      }
    },
    "subtask_2": {
      "responses": {
        "query_id_1": "response_text"
      }
    },
    "metadata": {},
    "score": 0.95
  },
  "model_name_2": {
    "subtask_1": {
      "responses": {
        "query_id_1": "response_text",
        "query_id_2": "response_text"
      }
    }
  }
};
